/**
 * Geometry for a draggable launcher bubble. Pure calculations only — the
 * component owns the DOM reads and the motion values, so every rule here is
 * unit-testable under vitest's node environment.
 *
 * `x`/`y` throughout address the **box top-left** in viewport pixels, because
 * that is exactly what a bubble's motion values drive.
 */

export type Viewport = { width: number; height: number };
export type Point = { x: number; y: number };
export type Bounds = { minX: number; maxX: number; minY: number; maxY: number };

/** Which side the bubble is docked to. Vertical position is free. */
export type BubbleAnchor = "left" | "right";

/**
 * Viewport-independent persisted form. `yRatio` is 0..1 of the vertical travel
 * range rather than a pixel offset, so a bottom-parked bubble stays
 * bottom-parked when the window gets shorter.
 */
export type StoredBubblePosition = { anchor: BubbleAnchor; yRatio: number };

/**
 * One bubble's own measurements.
 *
 * `box` is the element's layout box; `inset` is the distance from that box to
 * the *visible* circle on each side, which is non-zero only when the element
 * carries drop-shadow padding (the Birdie SVG does; a CSS `box-shadow` does
 * not, because it takes no layout space). Every gap below is measured to the
 * circle, never to the box, or the bubble reads as misaligned.
 */
export type BubbleMetrics = {
  box: { width: number; height: number };
  inset: { top: number; right: number; bottom: number; left: number };
  /** Diameter of the visible circle — decides which half of the screen it is on. */
  circle: number;
  /** Gap between the visible circle and a viewport edge. */
  edgeGap: number;
  /** Extra top gap so the bubble clears this surface's app header. */
  topGap: number;
  /** Resting gap above the bottom with nothing stored. */
  defaultBottomGap: number;
};

/** The frame a bubble is positioned in: where it lives, and how big it is. */
export type BubbleFrame = { viewport: Viewport; metrics: BubbleMetrics };

/**
 * Arrow-key nudge distance (px). Shift multiplies it — see `NUDGE_STEP_LARGE`.
 * Not a `BubbleMetrics` field: a keyboard step is a shared interaction
 * constant, not a per-bubble measurement.
 */
export const NUDGE_STEP = 24;
export const NUDGE_STEP_LARGE = 96;

/** Per-ms decay; factor = D / (1 - D) / 1000 ~= 0.199, so 1000 px/s carries ~199px. */
const DECELERATION = 0.995;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/** Guards every public entry point against a corrupted store or a freak sample. */
const finiteOr = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

/**
 * Travel bounds for the box top-left. A viewport narrower (or shorter) than
 * the bubble plus its gaps collapses the range to a single point rather than
 * inverting — callers clamp against these and must never see `min > max`.
 */
export function getBubbleBounds(frame: BubbleFrame): Bounds {
  const { viewport, metrics } = frame;
  const width = finiteOr(viewport?.width, 0);
  const height = finiteOr(viewport?.height, 0);

  const minX = metrics.edgeGap - metrics.inset.left;
  const maxX =
    width - metrics.edgeGap - (metrics.box.width - metrics.inset.right);
  const minY = metrics.topGap - metrics.inset.top;
  const maxY =
    height - metrics.edgeGap - (metrics.box.height - metrics.inset.bottom);

  return {
    minX,
    maxX: Math.max(minX, maxX),
    minY,
    maxY: Math.max(minY, maxY),
  };
}

/** Where the bubble sits with nothing stored: right side, at `defaultBottomGap`. */
function getDefaultPosition(frame: BubbleFrame): Point {
  const { viewport, metrics } = frame;
  const bounds = getBubbleBounds(frame);
  const height = finiteOr(viewport?.height, 0);
  const y =
    height -
    metrics.defaultBottomGap -
    (metrics.box.height - metrics.inset.bottom);

  return { x: bounds.maxX, y: clamp(y, bounds.minY, bounds.maxY) };
}

/** Anything the store hands back that isn't a usable position falls back to the default. */
function isValidStored(
  stored: StoredBubblePosition | null | undefined,
): stored is StoredBubblePosition {
  if (!stored || typeof stored !== "object") return false;
  if (stored.anchor !== "left" && stored.anchor !== "right") return false;
  return (
    typeof stored.yRatio === "number" &&
    Number.isFinite(stored.yRatio) &&
    stored.yRatio >= 0 &&
    stored.yRatio <= 1
  );
}

/** Stored (or missing/corrupt) position + current frame -> concrete x/y. */
export function resolveBubblePosition(
  stored: StoredBubblePosition | null | undefined,
  frame: BubbleFrame,
): Point {
  if (!isValidStored(stored)) return getDefaultPosition(frame);

  const bounds = getBubbleBounds(frame);
  return {
    x: stored.anchor === "left" ? bounds.minX : bounds.maxX,
    y: bounds.minY + stored.yRatio * (bounds.maxY - bounds.minY),
  };
}

/** Concrete x/y + frame -> the viewport-independent form to persist. */
export function toStoredBubblePosition(
  point: Point,
  frame: BubbleFrame,
): StoredBubblePosition {
  const bounds = getBubbleBounds(frame);
  const x = finiteOr(point?.x, bounds.maxX);
  const y = finiteOr(point?.y, bounds.maxY);

  const travel = bounds.maxY - bounds.minY;
  // A viewport too short for any vertical travel has no ratio to record.
  const yRatio = travel > 0 ? clamp((y - bounds.minY) / travel, 0, 1) : 0;

  return { anchor: resolveAnchor(x, frame), yRatio };
}

/** Which side of the viewport the bubble's visible circle is centred on. */
function resolveAnchor(x: number, frame: BubbleFrame): BubbleAnchor {
  const { viewport, metrics } = frame;
  const width = finiteOr(viewport?.width, 0);
  const circleCenterX = x + metrics.inset.left + metrics.circle / 2;
  // Dead-centre ties go left, deterministically — see the test that pins it.
  return circleCenterX <= width / 2 ? "left" : "right";
}

/**
 * How far a flick would carry, using the standard scroll-deceleration model.
 * Clamped to one viewport dimension so a freak velocity sample can't send the
 * bubble to infinity.
 */
function projectVelocity(velocity: number, limit: number): number {
  const projected =
    (finiteOr(velocity, 0) / 1000) * (DECELERATION / (1 - DECELERATION));
  return Number.isFinite(projected) ? clamp(projected, -limit, limit) : 0;
}

/**
 * Drag release: pick the nearer side, dock to it, keep the drop height.
 *
 * Horizontal position is never free — the bubble always ends up against an
 * edge (iOS AssistiveTouch behavior), which is what keeps it off the content
 * underneath. Velocity only steers that side choice, by projecting where the
 * flick was headed; it is deliberately NOT applied to the vertical axis, where
 * it would fling the bubble hundreds of pixels away from where it was dropped.
 */
export function resolveDragRelease(
  point: Point,
  velocity: Point,
  frame: BubbleFrame,
): Point & { anchor: BubbleAnchor } {
  const bounds = getBubbleBounds(frame);
  const width = finiteOr(frame.viewport?.width, 0);

  const projectedX =
    finiteOr(point?.x, bounds.maxX) + projectVelocity(velocity?.x, width);
  const anchor = resolveAnchor(projectedX, frame);

  return {
    anchor,
    x: anchor === "left" ? bounds.minX : bounds.maxX,
    y: clamp(finiteOr(point?.y, bounds.maxY), bounds.minY, bounds.maxY),
  };
}

/**
 * Keyboard nudge. Unlike a drag release this does NOT dock horizontally — the
 * point of arrow keys is fine positioning, and `Home`/`End` already dock.
 *
 * `delta` is an offset, not a target: `{ x: 0, y: -NUDGE_STEP }` moves up.
 */
export function nudgeBubblePosition(
  point: Point,
  delta: Point,
  frame: BubbleFrame,
): Point {
  const bounds = getBubbleBounds(frame);
  return {
    x: clamp(
      finiteOr(point?.x, bounds.maxX) + finiteOr(delta?.x, 0),
      bounds.minX,
      bounds.maxX,
    ),
    y: clamp(
      finiteOr(point?.y, bounds.maxY) + finiteOr(delta?.y, 0),
      bounds.minY,
      bounds.maxY,
    ),
  };
}

/** Dock to a given side without changing height — backs `Home`/`End`. */
export function dockBubblePosition(
  point: Point,
  anchor: BubbleAnchor,
  frame: BubbleFrame,
): Point {
  const bounds = getBubbleBounds(frame);
  return {
    x: anchor === "left" ? bounds.minX : bounds.maxX,
    y: clamp(finiteOr(point?.y, bounds.maxY), bounds.minY, bounds.maxY),
  };
}
